import { Component } from '@theme/component';

class RareBreedHeader extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.update = () => document.body.style.setProperty('--rb-masthead-height', `${this.offsetHeight}px`);
    this.resizeObserver = new ResizeObserver(this.update);
    this.resizeObserver.observe(this);
    window.addEventListener('resize', () => { if (innerWidth >= 750) this.closeMenu(); this.update(); }, options);
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeMenu(true);
    }, options);
    this.addEventListener('click', (event) => {
      if (event.target.closest('a')) this.closeMenu();
    }, options);
    this.update();
  }
  toggleMenu() {
    const open = this.refs.toggle.getAttribute('aria-expanded') !== 'true';
    this.refs.toggle.setAttribute('aria-expanded', String(open));
    this.refs.menu.hidden = !open;
    this.update();
  }
  closeMenu(focus = false) {
    this.refs.toggle.setAttribute('aria-expanded', 'false');
    this.refs.menu.hidden = true;
    this.update();
    if (focus) this.refs.toggle.focus();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.abort?.abort();
    this.resizeObserver?.disconnect();
  }
}

class RareBreedMotion extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.abort = new AbortController();
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    const kind = this.dataset.motion;
    if (kind === 'claims') {
      this.startScrollClaims();
      return;
    }
    if (kind === 'social') {
      if (this.dataset.autoplay === 'true') this.startPlayback();
      return;
    }
    this.update = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        const box = this.getBoundingClientRect();
        const reduced = this.motion.matches;
        if (this.dataset.motion === 'hero') {
          this.style.setProperty('--parallax', `${reduced ? 0 : Math.min(55, Math.max(0, -box.top * 0.08))}px`);
        }
      });
    };
    const options = { signal: this.abort.signal, passive: true };
    window.addEventListener('scroll', this.update, options);
    window.addEventListener('resize', this.update, options);
    this.motion.addEventListener('change', this.update, { signal: this.abort.signal });
    this.update();
  }
  startScrollClaims() {
    const words = [...this.querySelectorAll('[data-claim]')];
    let active = -1;
    let viewportWidth = innerWidth;
    let viewportHeight = innerHeight;
    const update = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        const reduced = this.motion.matches;
        this.classList.toggle('has-scroll-highlight', !reduced);
        if (innerWidth !== viewportWidth || innerWidth >= 750) {
          viewportWidth = innerWidth;
          viewportHeight = innerHeight;
        }
        let candidate = 0;
        let closest = Infinity;
        const boxes = words.map(word => word.getBoundingClientRect());
        const distances = boxes.map(box => Math.abs(box.top + box.height / 2 - viewportHeight * 0.55));
        words.forEach((word, index) => {
          if (distances[index] < closest) { closest = distances[index]; candidate = index; }
        });
        const buffer = innerWidth < 750 ? Math.min(12, (boxes[candidate]?.height || 0) * 0.25) : 0;
        if (active < 0 || distances[active] > closest + buffer) active = candidate;
        words.forEach((word, index) => word.classList.toggle('is-active', !reduced && index === active));
      });
    };
    const options = { signal: this.abort.signal, passive: true };
    window.addEventListener('scroll', update, options);
    window.addEventListener('resize', update, options);
    this.motion.addEventListener('change', update, options);
    this.resizeObserver = new ResizeObserver(update);
    this.resizeObserver.observe(this);
    update();
  }
  startPlayback() {
    const options = { signal: this.abort.signal };
    const track = this.querySelector('.rb-social__track');
    const posts = track ? [...track.children] : [];
    let previous = null;
    let visible = false;
    let hovered = false;
    let focused = false;
    let paused = false;
    let offset = 0;
    let cycle = 0;
    this.copies = [];
    const control = document.createElement('button');
    this.control = control;
    control.type = 'button';
    control.className = 'rb-motion-toggle';
    const updateControl = () => {
      const label = paused ? this.dataset.resumeLabel : this.dataset.pauseLabel;
      control.textContent = label;
      control.setAttribute('aria-label', label);
      control.setAttribute('aria-pressed', String(paused));
    };
    updateControl();
    this.append(control);
    // One bounded copy of the configured posts creates a seamless end-to-start join.
    // Images retain native lazy loading; no feed requests or ever-growing DOM.
    const measure = () => {
      if (!track) return;
      if (visible && !this.motion.matches && !this.copies.length && track.scrollWidth > track.clientWidth) {
        this.copies = posts.map((post) => {
          const copy = post.cloneNode(true);
          copy.setAttribute('aria-hidden', 'true');
          copy.tabIndex = -1;
          for (const node of [copy, ...copy.querySelectorAll('*')]) {
            node.removeAttribute('id');
            node.removeAttribute('data-shopify-editor-block');
          }
          copy.querySelectorAll('img').forEach((img) => { img.loading = 'lazy'; });
          track.append(copy);
          return copy;
        });
      }
      cycle = this.copies.length ? this.copies[0].offsetLeft - posts[0].offsetLeft : 0;
      offset = track.scrollLeft;
    };
    const tick = (time) => {
      this.frame = null;
      const delta = previous === null ? 0 : Math.min(time - previous, 100);
      previous = time;
      if (cycle) {
        offset = (offset + delta * 0.025) % cycle;
        track.scrollLeft = offset;
      }
      this.frame = requestAnimationFrame(tick);
    };
    const sync = () => {
      cancelAnimationFrame(this.frame);
      this.frame = null;
      previous = null;
      control.hidden = this.motion.matches || !cycle;
      if (visible && !document.hidden && !this.motion.matches && !paused && !hovered && !focused) {
        offset = track?.scrollLeft || 0;
        this.frame = requestAnimationFrame(tick);
      }
    };
    control.addEventListener('click', () => { clearTimeout(this.resumeTimer); paused = !paused; updateControl(); sync(); }, options);
    track?.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') { hovered = true; sync(); } }, options);
    track?.addEventListener('pointerleave', () => { hovered = false; sync(); }, options);
    this.addEventListener('focusin', (event) => { focused = event.target !== control && event.target.matches(':focus-visible'); sync(); }, options);
    this.addEventListener('focusout', (event) => { focused = this.contains(event.relatedTarget) && event.relatedTarget !== control && event.relatedTarget.matches(':focus-visible'); sync(); }, options);
    if (track) {
      for (const type of ['pointerdown', 'wheel']) {
        track.addEventListener(type, () => {
          paused = true;
          clearTimeout(this.resumeTimer);
          this.resumeTimer = setTimeout(() => { paused = false; updateControl(); sync(); }, 3000);
          updateControl(); sync();
        }, { ...options, passive: true });
      }
    }
    this.observer = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; measure(); sync(); });
    this.observer.observe(this);
    this.resizeObserver = new ResizeObserver(() => { measure(); sync(); });
    this.resizeObserver.observe(track || this);
    this.motion.addEventListener('change', () => {
      if (this.motion.matches) {
        this.copies.forEach((copy) => copy.remove());
        this.copies = [];
        if (track) track.scrollLeft = 0;
      }
      measure(); sync();
    }, options);
    document.addEventListener('visibilitychange', sync, options);
    sync();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.abort?.abort();
    cancelAnimationFrame(this.frame);
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
    this.control?.remove();
    clearTimeout(this.resumeTimer);
    this.copies?.forEach((copy) => copy.remove());
    this.frame = null;
  }
}

if (!customElements.get('rb-header')) customElements.define('rb-header', RareBreedHeader);
if (!customElements.get('rb-motion')) customElements.define('rb-motion', RareBreedMotion);
